import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
	Box,
	Table,
	TableBody,
	Typography,
	CircularProgress,
	useMediaQuery,
	Stack
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Pagination } from '@/components/Pagination';
import { showError } from '@/utils';
import { useShallowEqualSelector } from '@/hooks';
import { setPage } from '@/store/billing';
import { fetchPayments } from '@/store/billing/thunks';
import { Payment } from './Payment';
import { IncompleteBillingModal } from './IncompleteBillingModal';
import { useHandleRetry } from './hooks';

export function BillingHistory(props) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const handleRetry = useHandleRetry();
	const [incompleteOpen, setIncompleteOpen] = useState(false);

	const {
		completed,
		loading,
		page,
		rows = [],
		totalPages
	} = useShallowEqualSelector(({ billing }) => ({
		completed: billing.fetchPayments.completed,
		loading: billing.fetchPayments.loading,
		page: billing.page,
		rows: billing.rows,
		pagination: billing.totalPages
	}));

	const smDown = useMediaQuery(theme => theme.breakpoints.down('sm'), {
		noSsr: true
	});

	const pageSize = 12;

	const handleIncompleteModalOpen = () => setIncompleteOpen(true);

	const handleIncompleteModalClose = () => setIncompleteOpen(false);

	useEffect(() => {
		(async () => {
			try {
				await dispatch(fetchPayments(pageSize)).unwrap();
			} catch (error) {
				showError(error);
			}
		})();
	}, [page]);

	const TableBodyComponent = smDown ? Stack : TableBody;
	const TableComponent = smDown ? Box : Table;

	const isServiceFirst = !!rows?.[0]?.service;
	const firstValidIndex = isServiceFirst ? 1 : 0;

	return (
		<Box {...props}>
			<IncompleteBillingModal
				open={incompleteOpen}
				handleClose={handleIncompleteModalClose}
			/>
			<Typography variant="body2" fontWeight="bold" mb={2}>
				{t('billing.history.title')}
			</Typography>
			{!completed && loading && (
				<Box textAlign="center">
					<CircularProgress />
				</Box>
			)}
			<TableComponent>
				<TableBodyComponent gap={3}>
					{rows.map((payment, index) => (
						<Payment
							key={`${payment.id}-${index}`}
							{...payment}
							onRetry={handleRetry}
							isLastPayment={index === firstValidIndex && page === 1}
							handleIncompleteModalOpen={handleIncompleteModalOpen}
							index={index}
						/>
					))}
				</TableBodyComponent>
			</TableComponent>
			{totalPages > 1 && (
				<Pagination
					count={totalPages}
					page={page}
					onChange={(_, value) => dispatch(setPage(value))}
				/>
			)}
		</Box>
	);
}
